import { useState, useEffect, useCallback, useRef } from 'react'

export type GPSSamplerState = 'idle' | 'acquiring' | 'success' | 'error'

export interface GPSCoords {
  latitude: number
  longitude: number
}

export interface GPSSamplerResult {
  state: GPSSamplerState
  coords: GPSCoords | null
  retry: () => void
}

const SAMPLE_COUNT = 3
const SAMPLE_INTERVAL_MS = 1000
const GEO_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  timeout: 10_000,
  maximumAge: 0,
}

function bestAccuracy(readings: GeolocationPosition[]): GeolocationPosition {
  return readings.reduce((best, curr) =>
    curr.coords.accuracy < best.coords.accuracy ? curr : best
  )
}

function averageCoords(readings: GeolocationPosition[]): GPSCoords {
  const best = bestAccuracy(readings)
  const lat = readings.reduce((sum, r) => sum + r.coords.latitude, 0) / readings.length
  const lng = readings.reduce((sum, r) => sum + r.coords.longitude, 0) / readings.length
  const maxAcceptableAccuracy = best.coords.accuracy * 3
  const goodReadings = readings.filter(r => r.coords.accuracy <= maxAcceptableAccuracy)
  if (goodReadings.length === 0) return { latitude: lat, longitude: lng }
  return {
    latitude: goodReadings.reduce((s, r) => s + r.coords.latitude, 0) / goodReadings.length,
    longitude: goodReadings.reduce((s, r) => s + r.coords.longitude, 0) / goodReadings.length,
  }
}

export function useGPSSampler(): GPSSamplerResult {
  const [state, setState] = useState<GPSSamplerState>('idle')
  const [coords, setCoords] = useState<GPSCoords | null>(null)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const sample = useCallback(() => {
    if (!navigator.geolocation) {
      if (mountedRef.current) setState('error')
      return
    }

    if (mountedRef.current) {
      setState('acquiring')
      setCoords(null)
    }

    const readings: GeolocationPosition[] = []

    const collectSample = (index: number) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          readings.push(position)
          if (readings.length < SAMPLE_COUNT && index < SAMPLE_COUNT - 1) {
            setTimeout(() => collectSample(index + 1), SAMPLE_INTERVAL_MS)
          } else {
            if (mountedRef.current) {
              setCoords(averageCoords(readings))
              setState('success')
            }
          }
        },
        () => {
          if (!mountedRef.current) return
          if (readings.length > 0) {
            setCoords(averageCoords(readings))
            setState('success')
          } else {
            setState('error')
          }
        },
        GEO_OPTIONS
      )
    }

    collectSample(0)
  }, [])

  useEffect(() => {
    sample()
  }, [sample])

  return { state, coords, retry: sample }
}
